import { MeetChatImageAttachment, MeetChatMessage, MeetChatOutgoingMessage, MeetChatTransportMessage } from './types';

const fallbackAuthorName = 'Участник';

export const isParticipantSystemLabel = (name: string | undefined, participantId?: string): boolean => {
  const trimmed = name?.trim() ?? '';

  if (!trimmed) {
    return true;
  }

  if (participantId && trimmed === participantId) {
    return true;
  }

  if (/^user\d+$/i.test(trimmed)) {
    return true;
  }

  if (!/\s/.test(trimmed) && trimmed.length >= 12 && /^[a-z0-9_-]+$/i.test(trimmed)) {
    return true;
  }

  return false;
};

export const pickReadableParticipantName = (
  participantId: string,
  ...candidates: (string | undefined)[]
): string => {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();

    if (trimmed && !isParticipantSystemLabel(trimmed, participantId)) {
      return stripRoleSuffix(trimmed);
    }
  }

  return '';
};

export interface MeetChatAuthorContext {
  isGroupConsultation: boolean;
  isPsychologist: boolean;
  localParticipantId: string;
  psychologistId?: number | null;
  participantDisplayNames?: Record<string, string>;
}

const hasRoleSuffix = (name: string, role: 'psychologist' | 'client') => {
  const normalized = name.trim().toLowerCase();

  return normalized.endsWith(`- ${role}`) || normalized.endsWith(`-${role}`);
};

const stripRoleSuffix = (name: string) =>
  name
    .replace(/\s*-\s*psychologist\s*$/i, '')
    .replace(/\s*-\s*client\s*$/i, '')
    .trim();

export const isMeetChatPsychologistParticipant = (
  participantId: string,
  displayName: string,
  psychologistId?: number | null,
) =>
  (psychologistId != null && participantId === String(psychologistId)) || hasRoleSuffix(displayName, 'psychologist');

export const resolveMeetChatAuthorName = (
  transportMessage: MeetChatTransportMessage,
  context: MeetChatAuthorContext,
): string => {
  const { senderId, senderName } = transportMessage;
  const {
    isGroupConsultation,
    isPsychologist,
    localParticipantId,
    psychologistId,
    participantDisplayNames = {},
  } = context;

  if (senderId === localParticipantId) {
    return 'Я';
  }

  const resolvedDisplay = pickReadableParticipantName(
    senderId,
    participantDisplayNames[senderId],
    senderName,
  );
  const isSenderPsychologist = isMeetChatPsychologistParticipant(
    senderId,
    resolvedDisplay || senderName || '',
    psychologistId,
  );
  const realName = resolvedDisplay || fallbackAuthorName;

  if (isGroupConsultation) {
    return realName;
  }

  if (isPsychologist && !isSenderPsychologist) {
    return 'Клиент';
  }

  if (!isPsychologist && isSenderPsychologist) {
    return 'Психолог';
  }

  return realName;
};

const createMessageId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createMeetChatOutgoingMessage = (text: string, senderName?: string): MeetChatOutgoingMessage => ({
  id: createMessageId(),
  kind: 'text',
  text: text.trim(),
  sentAt: new Date().toISOString(),
  senderName: senderName?.trim() || undefined,
});

export const createMeetChatOutgoingImageMessage = (
  image: MeetChatImageAttachment,
  senderName?: string,
): MeetChatOutgoingMessage => ({
  id: createMessageId(),
  kind: 'image',
  text: '',
  sentAt: new Date().toISOString(),
  senderName: senderName?.trim() || undefined,
  image,
});

export const mapTransportMessageToMeetChatMessage = (
  transportMessage: MeetChatTransportMessage,
  authorContext: MeetChatAuthorContext,
): MeetChatMessage => ({
  id: transportMessage.id,
  kind: transportMessage.kind,
  text: transportMessage.text,
  sentAt: transportMessage.sentAt,
  authorId: transportMessage.senderId,
  authorName: resolveMeetChatAuthorName(transportMessage, authorContext),
  wireSenderName: transportMessage.senderName,
  isOwn: transportMessage.senderId === authorContext.localParticipantId,
  image: transportMessage.image,
});

